<script lang="ts" setup>
import type { SectionWithIndex } from '~/types'

interface Props extends Pick<SectionWithIndex, 'collection' | 'index' | 'id' | 'title' | 'theme' | 'subtitle' | 'expand' | 'buttons' | 'description'> {}
const props = defineProps<Props>()
const sideIsSwapped = props.index % 2 === 0
</script>

<template>
  <div
    v-motion
    :visible="{ opacity: 1, y: 0 }"
    :initial="{ opacity: 0, y: 20 }"
    :class="[theme, sideIsSwapped ? 'rounded-l-3xl ml-auto' : 'rounded-r-3xl mr-auto']"
    class="gap-6 my-32 py-40 lg:py-64 px-6 md:px-10 lg:px-16 2xl:px-32 w-11/12 xl:w-10/12 flex flex-wrap items-center  relative"
  >
    <div class="w-full lg:flex-1">
      <div
        v-motion
        class="subtitle font-display font-bold uppercase text-2xl md:text-4xl lg:text-5xl xl:text-6xl"
        :visible="{ opacity: 1, rotate: 0, y: 0, x: 0, scale: 1 }"
        :initial="{ opacity: 0, rotate: 5, x: 20, scale: 0.5 }"
        :delay="100"
      >
        {{ props.subtitle }}
      </div>
      <HeadingText
        tag="h2"
        class="mb-6 heading"
      >
        {{ props.title }}
      </HeadingText>
      <PageButtons
        v-if="buttons?.length"
        :buttons="expand?.buttons"
        class="pt-3 md:pt-6 lg:pt-12"
        :background="theme"
      />
    </div>
    <div
      v-motion
      :visible="{ opacity: 1, scale: 1 }"
      :initial="{ opacity: 0, scale: 0.2 }"
      :delay="500"
      class="absolute  text-lightblue lg:left-[35%] z-[2] transform"
      :class="[sideIsSwapped ? 'right-[1rem] top-[6rem] lg:top-auto lg:bottom-[10rem]' : 'right-[1rem] top-[6rem] lg:top-[10rem]']"
    >
      <Icon
        name="gbf:arrow-swish-left"
        class="w-24 h-12 lg:w-40 lg:h-24 arrow"
        :class="[sideIsSwapped ? 'lg:rotate-180' : '-rotate-[70] flip']"
        :style="[sideIsSwapped ? 'lg:flip' : '']"
      />
    </div>
    <div
      v-motion
      class="text text-lg lg:text-xl w-full lg:flex-1 paragraph"
      :visible="{ opacity: 1, rotate: 0, y: 0, x: 0, scale: 1 }"
      :initial="{ opacity: 0, rotate: 5, y: 20, x: 30, scale: 0.5 }"
      :delay="400"
    >
      <section
        class="htmldescription"
        data-allow-mismatch
        v-html="props.description"
      />
    </div>
  </div>
</template>

<style scoped>
.paragraph ::v-deep(p) {
  @apply pb-4;
}
.blue {
  @apply bg-gradient-to-tl from-blue from-50% to-pink text-white;
}
.blue .heading {
  @apply text-white;
}
.blue .subtitle {
  @apply text-yellow;
}
.blue .arrow {
  @apply text-pink;
}
.light-blue {
  @apply bg-gradient-to-tl from-lightblue from-80% to-blue text-primary;
}
.light-blue .heading {
  @apply text-white;
}
.light-blue .subtitle {
  @apply text-yellow;
}
.light-blue .arrow {
  @apply text-pink;
}
.pink {
  @apply bg-gradient-to-tl from-pink from-60% via-pink via-60% to-lightblue text-white;
}
.pink .heading {
  @apply text-white;
}
.pink .subtitle {
  @apply text-yellow;
}
.pink .arrow {
  @apply text-yellow;
}
</style>
